/**
 * Drill generators for practice modes.
 * All generation is deterministic given a seed, so tests can assert output.
 */

export const COMMON_WORDS = Object.freeze([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'any', 'can',
  'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him',
  'his', 'how', 'man', 'new', 'now', 'old', 'see', 'two', 'way', 'who',
  'boy', 'did', 'its', 'let', 'put', 'say', 'she', 'too', 'use', 'that',
  'with', 'have', 'this', 'will', 'your', 'from', 'they', 'know', 'want', 'been',
  'good', 'much', 'some', 'time', 'very', 'when', 'come', 'here', 'just', 'like',
  'long', 'make', 'many', 'more', 'only', 'over', 'such', 'take', 'than', 'them',
  'well', 'were', 'what', 'work', 'year', 'back', 'call', 'came', 'each', 'even',
]);

/**
 * Deterministic PRNG (mulberry32). Not cryptographically secure; used only
 * for reproducible drill generation.
 * @param {number} seed
 */
function makeRng(seed) {
  let t = seed >>> 0;
  return function rng() {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Generates a common-words drill.
 * @param {{ count?: number, seed?: number }} opts
 * @returns {string}
 */
export function generateCommonWordsDrill({ count = 50, seed = 1 } = {}) {
  const rng = makeRng(seed);
  const words = [];
  for (let i = 0; i < count; i++) {
    words.push(COMMON_WORDS[Math.floor(rng() * COMMON_WORDS.length)]);
  }
  return words.join(' ');
}

/**
 * Generates a weak-key drill: a sequence of random 3–6 letter fragments drawn
 * from the supplied keys. Not natural English — deliberately drills muscle memory.
 * @param {{ keys: string[], length?: number, seed?: number }} opts
 * @returns {string}
 */
export function generateWeakKeyDrill({ keys, length = 150, seed = 1 }) {
  if (!keys || keys.length < 3) {
    throw new Error('generateWeakKeyDrill requires at least 3 keys');
  }
  const rng = makeRng(seed);
  const fragments = [];
  let total = 0;
  while (total < length) {
    const fragLen = 3 + Math.floor(rng() * 4);
    let frag = '';
    for (let i = 0; i < fragLen; i++) {
      frag += keys[Math.floor(rng() * keys.length)];
    }
    fragments.push(frag);
    total += frag.length + 1; // account for space
  }
  return fragments.join(' ').slice(0, length);
}